// client/src/services/retryStrategy.ts
// Smart retry logic with exponential backoff, jitter, and circuit breaker

export interface RetryConfig {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  jitterFactor: number; // 0-1, adds randomness to prevent thundering herd
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 5,
  initialDelayMs: 100,
  maxDelayMs: 10000,
  backoffMultiplier: 2,
  jitterFactor: 0.1,
};

class CircuitBreaker {
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  private failureCount = 0;
  private successCount = 0;
  private lastFailureTime = 0;
  private readonly failureThreshold = 5;
  private readonly successThreshold = 2;
  private readonly resetTimeoutMs = 60000; // 1 minute

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime;
      if (timeSinceLastFailure > this.resetTimeoutMs) {        this.state = 'half-open';
      } else {
        throw new Error(
          `Circuit breaker is open. Retry in ${this.resetTimeoutMs - timeSinceLastFailure}ms`
        );
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      throw error;
    }
  }

  private onSuccess() {
    this.failureCount = 0;
    if (this.state === 'half-open') {
      this.successCount++;
      if (this.successCount >= this.successThreshold) {        this.state = 'closed';
        this.successCount = 0;
      }
    }
  }

  private onFailure() {
    this.lastFailureTime = Date.now();
    this.failureCount++;
    this.successCount = 0;

    if (this.failureCount >= this.failureThreshold) {
      console.error('[CircuitBreaker] Circuit opened - too many failures');
      this.state = 'open';
    }
  }

  getState() {
    return this.state;
  }

  reset() {
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
  }
}

class RetryStrategy {
  private circuitBreaker = new CircuitBreaker();
  private config: RetryConfig;
  private initialized = false;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;  }

  async executeWithRetry<T>(
    fn: () => Promise<T>,
    context: string = 'operation'
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      try {
        return await this.circuitBreaker.execute(fn);
      } catch (error) {
        console.warn(
          `[RetryStrategy] ${context} failed (attempt ${attempt}/${this.config.maxAttempts}):`,
          error
        );

        if (attempt < this.config.maxAttempts) {
          const delay = this.calculateDelay(attempt);          await this.sleep(delay);
        }
      }
    }

    throw lastError || new Error(`${context} failed after ${this.config.maxAttempts} attempts`);
  }

  private calculateDelay(attempt: number): number {
    // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
    const exponential = Math.min(
      this.config.initialDelayMs * Math.pow(this.config.backoffMultiplier, attempt - 1),
      this.config.maxDelayMs
    );

    // Add jitter to prevent thundering herd
    const jitter = exponential * this.config.jitterFactor * Math.random();
    return Math.floor(exponential + jitter);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getCircuitBreakerState() {
    return this.circuitBreaker.getState();
  }

  resetCircuitBreaker() {
    this.circuitBreaker.reset();
  }
}

export const retryStrategy = new RetryStrategy();

